import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Loader2, Users, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EVENT_CATEGORIES = [
  { id: 'site_survey', label: 'Site Survey', color: '#D4AF37' },
  { id: 'installation', label: 'Installation', color: '#60a5fa' },
  { id: 'maintenance', label: 'Maintenance', color: '#2dd4bf' },
  { id: 'cleaning', label: 'Cleaning', color: '#c084fc' },
  { id: 'meeting', label: 'Meeting', color: '#fb923c' },
  { id: 'other', label: 'Other', color: '#9ca3af' },
];
const catColor = (id) => EVENT_CATEGORIES.find(c => c.id === id)?.color || '#9ca3af';
const catLabel = (id) => EVENT_CATEGORIES.find(c => c.id === id)?.label || 'Other';

const emptyForm = {
  title: '', date: '', time: '', end_time: '', category: 'site_survey',
  client_email: '', client_name: '', staff_ids: [], notes: '', visible_to_client: false, status: 'scheduled',
};

function EventModal({ event, defaultDate, clients, staff, headers, onClose, onSaved }) {
  const isEdit = Boolean(event?.id);
  const [form, setForm] = useState(isEdit ? { ...emptyForm, ...event } : { ...emptyForm, date: defaultDate || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const selectClient = (email) => {
    const c = clients.find(x => x.email === email);
    setForm(p => ({ ...p, client_email: email, client_name: c?.name || '' }));
  };
  const toggleStaff = (id) => {
    setForm(p => ({ ...p, staff_ids: p.staff_ids.includes(id) ? p.staff_ids.filter(s => s !== id) : [...p.staff_ids, id] }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      if (isEdit) {
        const { id, staff_names, created_by, created_at, booking_id, visit_id, ...payload } = form;
        await axios.patch(`${API}/admin/calendar/events/${event.id}`, payload, { headers });
      } else {
        const { status, ...payload } = form;
        await axios.post(`${API}/admin/calendar/events`, payload, { headers });
      }
      onSaved();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/admin/calendar/events/${event.id}`, { headers });
      onSaved();
    } catch (err) { console.error(err); }
  };

  const inputCls = 'w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm';
  const labelCls = 'text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope mb-1.5 block';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="calendar-event-modal">
      <div className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-lg w-full p-7 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="close-event-modal"><X size={18} /></button>
        <h3 className="font-playfair text-xl text-white mb-5">{isEdit ? 'Edit Event' : 'New Event'}</h3>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelCls}>Title</label>
            <input type="text" required value={form.title} onChange={e => set('title', e.target.value)} className={inputCls} placeholder="Site survey — Smith residence" data-testid="event-title-input" />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_CATEGORIES.map(c => (
                <button key={c.id} type="button" onClick={() => set('category', c.id)}
                  className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${form.category === c.id ? 'text-black font-bold' : 'text-white/50 border-white/10 hover:border-white/30'}`}
                  style={form.category === c.id ? { backgroundColor: c.color, borderColor: c.color } : {}}
                  data-testid={`event-cat-${c.id}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" required value={form.date} onChange={e => set('date', e.target.value)} className={inputCls} data-testid="event-date-input" />
            </div>
            <div>
              <label className={labelCls}>Start</label>
              <input type="time" value={form.time} onChange={e => set('time', e.target.value)} className={inputCls} data-testid="event-time-input" />
            </div>
            <div>
              <label className={labelCls}>End</label>
              <input type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} className={inputCls} data-testid="event-endtime-input" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Client (optional)</label>
            <select value={form.client_email} onChange={e => selectClient(e.target.value)} className={inputCls} data-testid="event-client-select">
              <option value="">No client linked</option>
              {clients.map(c => <option key={c.id} value={c.email}>{c.name} ({c.email})</option>)}
            </select>
          </div>
          {staff.length > 0 && (
            <div>
              <label className={labelCls}><Users size={11} className="inline mr-1.5" />Assign Staff</label>
              <div className="flex flex-wrap gap-1.5">
                {staff.map(s => (
                  <button key={s.id} type="button" onClick={() => toggleStaff(s.id)}
                    className={`px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${form.staff_ids.includes(s.id) ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold' : 'text-white/50 border-white/10 hover:border-white/30'}`}
                    data-testid={`event-staff-${s.id}`}>
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className={labelCls}>Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} className={`${inputCls} resize-none`} data-testid="event-notes-input" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <button type="button" onClick={() => set('visible_to_client', !form.visible_to_client)}
              className={`flex items-center gap-2 px-3 py-2 rounded-sm font-manrope text-[10px] uppercase tracking-widest border transition-colors ${form.visible_to_client ? 'text-green-500 border-green-500/40 bg-green-500/10' : 'text-white/40 border-white/10'}`}
              data-testid="event-visibility-toggle">
              {form.visible_to_client ? <Eye size={13} /> : <EyeOff size={13} />}
              {form.visible_to_client ? 'Visible to client' : 'Internal only'}
            </button>
            {isEdit && (
              <select value={form.status} onChange={e => set('status', e.target.value)} className="bg-[#1a1a1a] border border-white/10 px-3 py-2 text-white font-manrope text-xs rounded-sm" data-testid="event-status-select">
                {['scheduled', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest py-3 rounded-sm hover:bg-[#C5A059] transition-colors disabled:opacity-50" data-testid="save-event-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : isEdit ? 'Save Changes' : 'Create Event'}
            </button>
            {isEdit && (
              <button type="button" onClick={remove} className="text-red-400/70 hover:text-red-400 border border-red-400/20 px-4 py-3 rounded-sm transition-colors" data-testid="delete-event-btn">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export function CalendarTab({ headers, clients, staff }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIdx, setMonthIdx] = useState(now.getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [modal, setModal] = useState(null); // { event } | { defaultDate }

  const monthStr = `${year}-${String(monthIdx + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month: monthStr });
      if (catFilter) params.set('category', catFilter);
      if (staffFilter) params.set('staff_id', staffFilter);
      const res = await axios.get(`${API}/admin/calendar/events?${params}`, { headers });
      setEvents(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStr, catFilter, staffFilter]);

  useEffect(() => { load(); }, [load]);

  const shift = (delta) => {
    const d = new Date(year, monthIdx + delta, 1);
    setYear(d.getFullYear());
    setMonthIdx(d.getMonth());
  };

  const firstDay = new Date(year, monthIdx, 1).getDay();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const eventsFor = (day) => events.filter(e => e.date === `${monthStr}-${String(day).padStart(2, '0')}`);
  const monthLabel = new Date(year, monthIdx, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div data-testid="calendar-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => shift(-1)} className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 rounded-sm transition-colors" data-testid="calendar-prev-month"><ChevronLeft size={16} /></button>
          <h3 className="font-playfair text-xl text-white min-w-[180px] text-center" data-testid="calendar-month-label">{monthLabel}</h3>
          <button onClick={() => shift(1)} className="w-9 h-9 flex items-center justify-center border border-white/10 text-white/50 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 rounded-sm transition-colors" data-testid="calendar-next-month"><ChevronRight size={16} /></button>
          {loading && <Loader2 size={16} className="text-[#D4AF37] animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-[#1a1a1a] border border-white/10 px-3 py-2 text-white/70 font-manrope text-xs rounded-sm" data-testid="calendar-category-filter">
            <option value="">All Categories</option>
            {EVENT_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} className="bg-[#1a1a1a] border border-white/10 px-3 py-2 text-white/70 font-manrope text-xs rounded-sm" data-testid="calendar-staff-filter">
            <option value="">All Staff</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={() => setModal({ defaultDate: todayStr })} className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors" data-testid="new-event-btn">
            <Plus size={14} /> New Event
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {EVENT_CATEGORIES.map(c => (
          <span key={c.id} className="flex items-center gap-1.5 text-white/40 font-manrope text-[10px] uppercase tracking-widest">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} /> {c.label}
          </span>
        ))}
      </div>

      {/* Grid */}
      <div className="bg-[#111113] border border-white/5 rounded-sm overflow-hidden" data-testid="calendar-grid">
        <div className="grid grid-cols-7 border-b border-white/5">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="px-2 py-2.5 text-center text-[10px] uppercase tracking-widest text-white/30 font-manrope">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, i) => {
            const dateStr = day ? `${monthStr}-${String(day).padStart(2, '0')}` : null;
            const dayEvents = day ? eventsFor(day) : [];
            return (
              <div key={i}
                onClick={() => day && setModal({ defaultDate: dateStr })}
                className={`min-h-[96px] border-b border-r border-white/5 p-1.5 ${day ? 'cursor-pointer hover:bg-white/[0.03]' : 'bg-white/[0.01]'} ${dateStr === todayStr ? 'bg-[#D4AF37]/5' : ''}`}
                data-testid={day ? `calendar-day-${dateStr}` : undefined}>
                {day && (
                  <>
                    <span className={`text-xs font-manrope ${dateStr === todayStr ? 'text-[#D4AF37] font-bold' : 'text-white/40'}`}>{day}</span>
                    <div className="mt-1 space-y-1">
                      {dayEvents.slice(0, 3).map(ev => (
                        <button key={ev.id}
                          onClick={(e) => { e.stopPropagation(); setModal({ event: ev }); }}
                          className={`w-full text-left px-1.5 py-1 rounded-sm text-[10px] font-manrope leading-tight truncate block ${ev.status === 'cancelled' ? 'line-through opacity-40' : ''}`}
                          style={{ backgroundColor: `${catColor(ev.category)}22`, color: catColor(ev.category), borderLeft: `2px solid ${catColor(ev.category)}` }}
                          title={`${ev.time ? ev.time + ' — ' : ''}${ev.title} (${catLabel(ev.category)})`}
                          data-testid={`calendar-event-${ev.id}`}>
                          {ev.time ? `${ev.time} ` : ''}{ev.title}
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="text-white/30 font-manrope text-[9px] px-1.5">+{dayEvents.length - 3} more</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <EventModal
          event={modal.event}
          defaultDate={modal.defaultDate}
          clients={clients}
          staff={staff}
          headers={headers}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
